/* src/modules/create_tutor/cards/CreateMultipleTutorsCard/index.js */

import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { capitalize, find, flatten, isEmpty, map, omit, toUpper } from 'lodash';

import { withStyles } from '@material-ui/core/styles';
import {
  CircularProgress,
  Button,
  Paper,
  Table, TableHead, TableRow, TableCell, TableBody,
  Typography,
} from '@material-ui/core';
import { Add, CloudUpload } from '@material-ui/icons';
import { Expand } from '../../../../app/components';

import styles from './styles';

import { parseCSV, parseCourseString } from '../../../../util';
import { getOrganization, getUniversity } from '../../selectors';
import { addTutors } from '../../actions';
import { searchCourses } from '../../../courses/actions';

import frenchPasswordUniversities from '../../french_password_universities';
import mentorshipPasswordOrganizations from '../../mentorship_password_organizations';

class CreateMultipleTutorsCard extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      file: null,
      loading: false,
      error: '',
      multi: false,
      tutors: [],
    };

    this._onSelectFile = this._onSelectFile.bind(this);
    this._onPressCreate = this._onPressCreate.bind(this);
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.selectedUniversity !== this.props.selectedUniversity)
      this.setState({ tutors: [] });
  }

  async _onSelectFile(event) {
    if (!event.target.files || !event.target.files[0]) {
      this.fileInput.value = '';
      this.setState({ file: null, error: '', tutors: [] });
      return;
    }
    this.setState({ file: event.target.files[0], loading: true });
    try {
      const data = await parseCSV(event.target.files[0]);
      const errors = [];
      const tutors = data.map(([first, last, email, courseString], i) => {
        if (!first || !last || !email || !courseString) {
          errors.push(i);
          return false;
        }

        const courses = parseCourseString(courseString);
        if (~courses.indexOf(false)) return false;

        return { first, last, email, courses, ignored: [] };
      }).filter(Boolean);

      await this.props.searchCourses({
        university: this.props.selectedUniversity,
        input: flatten(map(tutors, 'courses')),
      });

      // change all the courses to ids.
      // move all the courses that arent found to ignored
      const { searchedCourses } = this.props;
      tutors.forEach(({ courses }, i) => {
        const c = [];
        const ignored = [];
        courses.forEach(({ subject, number }) => {
          const courseString = `${subject.trim()} ${number.trim()}`;
          const f = find(searchedCourses, s => ~s._id.indexOf(courseString));
          if (!f || !f.result) ignored.push(courseString);
          else c.push({ code: `${f.result.subject} ${f.result.number}`, _id: { _course: f.result._id, status: 'APPROVED' } });
        });
        tutors[i].courses = c;
        tutors[i].ignored = ignored;
      });

      const nextState = { tutors, loading: false };
      if (!isEmpty(errors)) {
        nextState.error = errors.reduce((acc, x) => (acc ? `${acc}, ${x + 1}` : x + 1), '');
        nextState.multi = errors.length > 1;
      }

      this.setState(nextState);
    } catch (error) {
      console.log(error);
      this.setState({ loading: false });
    }
  }

  async _onPressCreate() {
    const { tutors } = this.state;
    this.setState({ loading: true });
    try {
      const params = {
        addTutorsInput: tutors.map(t => {
          const omitted = omit(t, 'ignored');
          omitted.courses = map(omitted.courses, '_id');
          return omitted;
        }),
        password: 'newtutor1',
        organization: this.props.selectedOrganization,
        university: this.props.selectedUniversity,
      };
      if (frenchPasswordUniversities.includes(this.props.selectedUniversity))
        params.password = 'nouveaututeur1';

      if (mentorshipPasswordOrganizations.includes(this.props.selectedOrganization))
        params.password = 'newmentor1';

      await this.props.addTutors(params);
    } catch (error) {
      this.fileInput.value = '';
      this.setState({ file: null, loading: false });
      return;
    }

    this.fileInput.value = '';
    this.setState({ tutors: [], file: null, loading: false, error: '' });
  }

  renderCreateButton(classes) {
    const { tutors } = this.state;
    const { selectedOrgName } = this.props;
    return (
      <Button
        disabled={isEmpty(tutors) || !selectedOrgName}
        color="primary"
        variant="raised"
        className={classes.button}
        onClick={this._onPressCreate}
      >
        <Add className={classes.rightIcon} />
        {'Create Tutors'}
      </Button>
    );
  }

  renderFileInfo(classes) {
    const { file } = this.state;
    const props = { className: classes.fileInformation, color: 'secondary' };
    let text = 'not selected';
    if (file) {
      props.color = undefined;
      text = file.name;
    }
    return (
      <Typography {...props}>
        {'file: '} {text}
      </Typography>
    );
  }

  renderFileError(classes) {
    const { error, multi } = this.state;
    if (!error) return null;
    return (
      <Typography color="secondary" className={classes.fileInformation}>
        {`line${multi ? 's' : ''} ${error} ha${multi ? 've' : 's'} formatting issues and will not be added to the database.`}
      </Typography>
    );
  }

  renderUploadButton(classes) {
    const { loading } = this.state;
    return (
      <React.Fragment>
        <input
          ref={node => this.fileInput = node}
          accept=".csv"
          className={classes.input}
          id="outlined-button-file"
          type="file"
          onChange={this._onSelectFile}
        />
        <label htmlFor="outlined-button-file">
          <Button variant="contained" component="span" color="default" className={classes.button}>
            <CloudUpload className={classes.leftIcon} />
            {'Upload'}
          </Button>
        </label>
        {loading && <CircularProgress className={classes.progress} size={24} />}
      </React.Fragment>
    );
  }

  renderTutors(classes) {
    const { tutors } = this.state;
    const data = tutors;
    const text = `Tutors to Add (${data.length})`;
    const props = { variant: 'subheading', className: classes.header };

    if (isEmpty(tutors)) return null;

    return (
      <React.Fragment>
        <Typography {...props}>
          {text}
        </Typography>
        <div className={classes.tableContainer}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Courses to Add</TableCell>
                <TableCell>Not Found</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map(({ first, last, email, courses, ignored }, i) => {
                const add = courses.reduce((acc, x) => (acc ? `${acc}, ${x.code}` : x.code), '');
                let notFound = ignored.reduce((acc, x) => (acc ? `${acc}, ${x}` : x), '');
                if (!notFound) notFound = 'none';
                return (
                  <TableRow key={`${email}-${i}`}>
                    <TableCell>{first} {last}</TableCell>
                    <TableCell>{email}</TableCell>
                    <TableCell>{add}</TableCell>
                    <TableCell>{notFound}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </React.Fragment>
    );
  }

  renderInfo(name, type, classes, gutterBottom) {
    const props = { variant: 'subheading', className: classes.orgText, gutterBottom };
    let text = name;
    if (!name) {
      props.color = 'secondary';
      text = `[NO ${toUpper(type)}]`;
    }
    return (
      <Typography {...props}>
        {`${capitalize(type)}: ${text}`}
      </Typography>
    );
  }

  renderOrganization(classes) {
    return this.renderInfo(this.props.selectedOrgName, 'organization', classes);
  }

  renderUniversity(classes) {
    return this.renderInfo(this.props.selectedUniName, 'university', classes, true);
  }

  renderInstructions(classes) {
    return (
      <Expand text="Expand to see csv format">
        <Typography variant="body2" className={classes.instructions}>
          {'csv file format:\n'}
        </Typography>
        <Typography variant="body1" className={classes.instructions}>
          {'[FIRST_NAME],[LAST_NAME],[EMAIL],[COURSES]\n\n'}
        </Typography>
        <div style={{ height: 12 }} />
        <Typography variant="body2" className={classes.instructions}>
          {'[COURSES] format:\n'}
        </Typography>
        <Typography variant="body1" className={classes.instructions}>
          {'[SUBJECT]|[NUMBER]&[NUMBER]&...&[NUMBER];[SUBJECT]|[NUMBER]&...'}
        </Typography>
        <div style={{ height: 12 }} />
        <Typography variant="body2" className={classes.instructions}>
          {'Example:'}
        </Typography>
        <Typography variant="body1" className={classes.instructions}>
          {'John,Smith,jsmith@nimbusapp.ca,MATH|133&140;COMP|202&250&273'}
        </Typography>
        <Typography variant="body1" className={classes.instructions}>
          {'Bob,Lee,blee@nimbusapp.ca,ECON|208;CEGL|406'}
        </Typography>
      </Expand>
    );
  }

  render() {
    const { classes } = this.props;
    return (
      <Paper>
        <div className={classes.spacing} />
        <Typography variant="title" className={classes.title}>
          {'Create Multiple Tutors'}
        </Typography>
        {this.renderOrganization(classes)}
        {this.renderUniversity(classes)}
        {this.renderInstructions(classes)}
        <div style={{ height: 12 }} />
        {this.renderFileInfo(classes)}
        {this.renderFileError(classes)}
        {this.renderTutors(classes)}
        <div className={classes.buttons}>
          {this.renderUploadButton(classes)}
          {this.renderCreateButton(classes)}
        </div>
      </Paper>
    );
  }
}

CreateMultipleTutorsCard.propTypes = {
  classes: PropTypes.object.isRequired,
  selectedOrgName: PropTypes.string.isRequired,
  selectedOrganization: PropTypes.string.isRequired,
  selectedUniName: PropTypes.string.isRequired,
  selectedUniversity: PropTypes.string.isRequired,
  addTutors: PropTypes.func.isRequired,
  searchCourses: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  selectedOrganization: state.auth.selectedOrganization,
  selectedUniversity: state.auth.selectedUniversity,
  selectedOrgName: getOrganization(state),
  selectedUniName: getUniversity(state),
  searchedCourses: state.courses.search.data,
});

const mapDispatchToProps = dispatch => ({
  addTutors: params => dispatch(addTutors(params)()),
  searchCourses: params => dispatch(searchCourses(params)()),
});

export default compose(
  withStyles(styles, { name: 'CreateMultipleTutorsCard' }),
  connect(mapStateToProps, mapDispatchToProps)
)(CreateMultipleTutorsCard);



// WEBPACK FOOTER //
// ./src/modules/create_tutor/cards/CreateMultipleTutorsCard/index.js